// File used to update cart actions header and line items
// ==============================
// Cart actions header
// - Ship Order Complete
// ==============================
// Line items
// - Ship Item Complete
// - Packing Notes
// - Requested Delivery Date
// ==============================

/**
 * Shopify Cart Updates for tracking ship complete toggles
 */
class CartMutations {
  constructor() {
    this.isUpdatingCart = false;
    this.pendingUpdate = null;
    this.isApplyingDeliveryDateFromCart = false;
    this.init();
  }

  init() {
    // Get current cart state and sync ship complete buttons
    this.syncWithCartState();
    this.syncWithCartLineItems();

    document.addEventListener('cart:rows-updated', () => {
      this.syncDeliveryDatesAfterDomUpdate();
    });

    document.addEventListener('cart:availability-updated', () => {
      this.updateShipCompleteBannerDate();
    });

    // Listen for ship complete toggle events
    this.setupShipCompleteListeners();
    this.wrapShipCompleteBannerShow();

    // Listen for preferred delivery date changes
    this.setupPreferredDeliveryDateListeners();

    // Listen for input delivery date errors
    this.setupDeliveryDateValidation();

    // Listen for line item quantity changes and update the quanity via API
    this.setupLineItemQuantityListeners();

    document.addEventListener('cart:rtp-pricing-ready', () => {
      this.validateCartQuantities?.();
    });

    // Add a mutation observer to the cart to update the realtime prices when the cart is updated
    this.setupRealtimePriceMutationObserver();

    // Validate quantities against min/step and block checkout if invalid
    this.setupQuantityValidation();
  }

  setupRealtimePriceMutationObserver() {
    const cartTableBody = document.querySelector('.cart-table__body');
    if (!cartTableBody) {
      console.warn('Cart table body not found, mutation observer not set up');
      return;
    }

    // Debounce function to prevent rapid concurrent calls
    let debounceTimeout;
    const debouncedUpdate = () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        this.handleRealtimePriceUpdate();
      }, 300); // Wait 300ms after last mutation before updating
    };

    // Store observer instance for potential cleanup
    this.realtimePriceObserver = new MutationObserver((mutations) => {
      debouncedUpdate();
    });

    // Observe changes to the cart table body and all its descendants
    this.realtimePriceObserver.observe(cartTableBody, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-realtime-price'],
    });
  }

  /**
   * Handle realtime price updates with queue management to prevent conflicts
   */
  async handleRealtimePriceUpdate() {
    // If already updating, queue this update
    if (this.isUpdatingCart) {
      this.pendingUpdate = true;
      return;
    }

    this.isUpdatingCart = true;
    this.pendingUpdate = false;

    try {
      // BPM-5360: realtime price overrides are set at add-to-cart; only payment terms need a cart attribute update here.
      await this.updateCartAttributesWithPaymentTerms();
    } catch (error) {
      console.error('Error updating realtime prices:', error);
    } finally {
      this.isUpdatingCart = false;

      // If there was a pending update, process it now
      if (this.pendingUpdate) {
        setTimeout(() => {
          this.handleRealtimePriceUpdate();
        }, 100);
      }
      // BPM-5360: re-check checkout eligibility after RTP settles (replaces post-cart price patching).
      if (typeof window.syncCartCheckoutFromCartPrices === 'function') {
        await window.syncCartCheckoutFromCartPrices();
      }
      this.validateCartQuantities?.();
      this.updateShipCompleteBannerDate();
    }
  }

  async updateCartAttributesWithPaymentTerms() {
    const paymentTerms = document.querySelector('.company-payment-terms');
    if (!paymentTerms) return;

    // Wait for payment terms value to be set (it's set asynchronously)
    // Timeout after 5 seconds
    const waitForPaymentTerms = () => {
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)
        const checkValue = () => {
          attempts++;
          if (paymentTerms.value) {
            resolve(paymentTerms.value);
          } else if (attempts >= maxAttempts) {
            console.warn('Payment terms value not set after timeout');
            resolve(null);
          } else {
            setTimeout(checkValue, 100);
          }
        };
        checkValue();
      });
    };

    try {
      const paymentTermsValue = await waitForPaymentTerms();
      if (!paymentTermsValue) return;

      await this.trackCartMutation(
        this.queueCartWrite(async () => {
          const cart = await this.getCurrentCartState();
          if (!(/** @type {any} */ (cart)?.item_count)) return;

          await this.writeCartAttributes({payment_terms: String(paymentTermsValue)});
        })
      );
    } catch (error) {
      console.error('Error updating cart attributes with payment terms:', error);
    }
  }

  /**
   * Parse the user's sales area external_id from the context cookie.
   * Mirrors the approach in rs-input-validation.js / rs-realtime-pricing.js.
   * @returns {string|null}
   */
  getSalesAreaExternalId() {
    try {
      const cookies = document.cookie.split(';').reduce((acc, c) => {
        const eqIdx = c.indexOf('=');
        if (eqIdx < 0) return acc;
        acc[decodeURIComponent(c.slice(0, eqIdx).trim())] = decodeURIComponent(c.slice(eqIdx + 1).trim());
        return acc;
      }, /** @type {Record<string,string>} */ ({}));
      const context = cookies.context ? JSON.parse(cookies.context) : null;
      const saRaw = Array.isArray(context?.sales_area)
        ? (context.sales_area[0]?.fields || context.sales_area[0])
        : (context?.sales_area?.fields || context?.sales_area || {});
      return saRaw?.external_id || null;
    } catch {
      return null;
    }
  }

  /**
   * For each cart row, read min/step from the data-rtp-sales attribute on the
   * availability cell and apply them to the row's quantity input.
   * This is needed because rs-quantity.liquid receives a line item (not a product)
   * in the cart context, so metafields can't be resolved server-side.
   */
  initCartQuantityConstraints() {
    const salesAreaId = this.getSalesAreaExternalId();

    document.querySelectorAll('.rs-table-body__row').forEach((row) => {
      const availCell = row.querySelector('[data-rtp-sales]');
      const quantityInput = row.querySelector('input.rs-quantity-input');
      if (!availCell || !quantityInput) return;

      try {
        const raw = availCell.getAttribute('data-rtp-sales') || '[]';
        const salesData = JSON.parse(raw);
        if (!Array.isArray(salesData) || !salesData.length) return;

        const entry = salesAreaId
          ? (salesData.find((d) => d?.sales_area === salesAreaId) || salesData[0])
          : salesData[0];
        if (!entry) return;

        const min = Number(entry.minimum_order_quantity);
        const step = Number(entry.ship_increment_quantity);

        if (min > 0) quantityInput.setAttribute('min', String(min));
        if (step > 0) quantityInput.setAttribute('step', String(step));
      } catch {
        // ignore malformed data
      }
    });
  }

  /**
   * Validate all cart quantity inputs against their min and step attributes.
   * Blocks the checkout link and shows error messages when values are invalid.
   * Called on load, after RTP updates, and on any quantity change.
   */
  setupQuantityValidation() {
    const tbody = document.querySelector('.cart-table__body');
    if (!tbody) return;

    const validateQuantities = () => {
      const salesAreaId = this.getSalesAreaExternalId();
      let hasErrors = false;

      tbody.querySelectorAll('.rs-table-body__cell--quantity').forEach((cell) => {
        const input = cell.querySelector('input.rs-quantity-input');
        if (!input || input.disabled) return;

        // Sync min/step from the row's sales data on every validation call so we
        // don't depend on initCartQuantityConstraints having run beforehand.
        const row = cell.closest('.rs-table-body__row');
        const availCell = row?.querySelector('[data-rtp-sales]');
        if (availCell) {
          try {
            const salesData = JSON.parse(availCell.getAttribute('data-rtp-sales') || '[]');
            if (Array.isArray(salesData) && salesData.length) {
              const entry = salesAreaId
                ? (salesData.find((d) => d?.sales_area === salesAreaId) || salesData[0])
                : salesData[0];
              if (entry) {
                const minVal = Number(entry.minimum_order_quantity);
                const stepVal = Number(entry.ship_increment_quantity);
                if (minVal > 0) input.setAttribute('min', String(minVal));
                if (stepVal > 0) input.setAttribute('step', String(stepVal));
              }
            }
          } catch { /* ignore malformed data */ }
        }

        const min = parseInt(input.min) || 1;
        const step = parseInt(input.step) || 1;
        const value = parseInt(input.value);
        const errorSpan = cell.querySelector('.rs-error__message-text--min-quantity');
        const errorMsg = cell.querySelector('.rs-error__message');

        if (isNaN(value) || value < min) {
          cell.classList.add('rs-error');
          if (errorMsg) errorMsg.style.display = 'flex';
          if (errorSpan) errorSpan.textContent = /** @type {any} */ (window).__t('minimum_order', { count: min });
          hasErrors = true;
        } else if (step > 1 && value % step !== 0) {
          cell.classList.add('rs-error');
          if (errorMsg) errorMsg.style.display = 'flex';
          if (errorSpan) errorSpan.textContent = /** @type {any} */ (window).__t('order_in_multiples', { count: step });
          hasErrors = true;
        } else {
          cell.classList.remove('rs-error');
          if (errorMsg) errorMsg.style.display = 'none';
        }
      });

      const checkoutLinks = Array.from(
        document.querySelectorAll('a.cart-your-order-summary__checkout, .cart-page-summary a')
      );
      checkoutLinks.forEach((checkoutLink) => {
        if (hasErrors) {
          if (!checkoutLink.dataset.originalHref && checkoutLink.getAttribute('href')) {
            checkoutLink.dataset.originalHref = checkoutLink.getAttribute('href') || '';
          }
          checkoutLink.removeAttribute('href');
          checkoutLink.setAttribute('aria-disabled', 'true');
          checkoutLink.classList.add('disabled');
        } else if (
          checkoutLink.dataset.rtpCheckoutBlocked === 'true' ||
          checkoutLink.dataset.rtpCheckoutLoading === 'true' ||
          checkoutLink.dataset.accountBlockCheckout === 'true' ||
          checkoutLink.getAttribute('data-cart-mutation-pending') === 'true'
        ) {
          if (!checkoutLink.dataset.originalHref && checkoutLink.getAttribute('href')) {
            checkoutLink.dataset.originalHref = checkoutLink.getAttribute('href') || '';
          }
          checkoutLink.removeAttribute('href');
          checkoutLink.setAttribute('aria-disabled', 'true');
          checkoutLink.classList.add('disabled');
        } else {
          if (checkoutLink.dataset.originalHref && !checkoutLink.getAttribute('href')) {
            checkoutLink.setAttribute('href', checkoutLink.dataset.originalHref);
          }
          checkoutLink.setAttribute('aria-disabled', 'false');
          checkoutLink.classList.remove('disabled');
        }
      });
    };

    const onQuantityInputEvent = (event) => {
      if (event.target instanceof HTMLInputElement && event.target.closest('.rs-table-body__cell--quantity')) {
        validateQuantities();
      }
    };

    tbody.addEventListener('change', onQuantityInputEvent);
    tbody.addEventListener('input', onQuantityInputEvent);
    tbody.addEventListener('quantity-selector:update', validateQuantities);

    // Capture-phase fallback in case events are stopped before reaching tbody.
    document.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.closest('.cart-table__body .rs-table-body__cell--quantity')) {
        validateQuantities();
      }
    }, { capture: true });

    document.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.closest('.cart-table__body .rs-table-body__cell--quantity')) {
        validateQuantities();
      }
    }, { capture: true });

    // Intercept checkout link clicks to validate and ensure all quantity changes are saved.
    Array.from(
      document.querySelectorAll('a.cart-your-order-summary__checkout, .cart-page-summary a')
    ).forEach((checkoutLink) => {
      checkoutLink.addEventListener('click', (e) => {
        validateQuantities();
        if (!checkoutLink.getAttribute('href')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        const checkoutFallback = `${(window.Shopify?.routes?.root || '/')}pages/checkout`;
        const href = checkoutLink.getAttribute('href') || checkoutLink.dataset.originalHref || checkoutFallback;

        // If a change-event-triggered POST is already in flight, wait for it
        if (this._pendingCartUpdate) {
          e.preventDefault();
          this._pendingCartUpdate
            .catch(() => {})
            .then(() => { window.location.href = href; });
          return;
        }

        // Catch stepper-based changes: input.value differs from server-rendered defaultValue
        const dirtyUpdates = [];
        document.querySelectorAll('.cart-table__body .rs-table-body__cell--quantity input.rs-quantity-input').forEach((input) => {
          if (!(input instanceof HTMLInputElement) || input.value === input.defaultValue) return;
          const row = input.closest('.rs-table-body__row');
          // Skip clone rows — one write per cart line covers every split, and a clone's own
          // quantity would otherwise overwrite the combined line total.
          if (row?.getAttribute('data-multiple-delivery') === 'true') return;

          const cartLine = this.getCartLineForRow(row);
          if (!cartLine) return;

          dirtyUpdates.push({ cartLine, quantity: input.value, input });
        });

        if (dirtyUpdates.length > 0) {
          e.preventDefault();
          Promise.all(
            dirtyUpdates.map(({ cartLine, quantity, input }) =>
              this.buildQuantityChangeBody(cartLine, quantity)
                .then((body) =>
                  fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                  })
                )
                .then(() => { input.defaultValue = quantity; })
                .catch(() => {})
            )
          ).then(() => { window.location.href = href; });
        }
      }, { capture: true });
    });

    // Run initial validation shortly after load so the checkout link is blocked
    // before the user can interact with it.
    setTimeout(validateQuantities, 300);

    this.validateCartQuantities = validateQuantities;
  }

  /**
   * Listen for line item quantity changes and update the quanity via API
   */
  setupLineItemQuantityListeners() {
    const tbody = document.querySelector('.cart-table__body');
    if (!tbody) return;

    // Typing only fires `change` on blur/Enter, so the cart (and the surcharge check) lagged until
    // the user clicked away. Commit a typed quantity once they pause, through the same `change` path.
    const TYPING_COMMIT_DELAY_MS = 800;
    let typingTimer;
    tbody.addEventListener('input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.closest('.rs-table-body__cell--quantity')) return;
      clearTimeout(typingTimer);
      typingTimer = setTimeout(() => {
        const value = parseInt(input.value, 10);
        const min = parseInt(input.min, 10) || 1;
        if (!Number.isFinite(value) || value < min || input.value === input.defaultValue) return;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, TYPING_COMMIT_DELAY_MS);
    });

    tbody.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.closest('.rs-table-body__cell--quantity')) return;
      clearTimeout(typingTimer);

      const row = input.closest('.rs-table-body__row');
      const cartLine = this.getCartLineForRow(row);
      if (!cartLine) return;

      const savedQuantity = input.value;
      document.dispatchEvent(new CustomEvent('cart:line-quantity-changing'));
      const updatePromise = (async () => {
        try {
          // Split rows share one Shopify line: every split's date and quantity lives in
          // `_requested_deliveries`, and the line quantity is their total.
          const cartChangeBody = await this.buildQuantityChangeBody(cartLine, savedQuantity);

          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartChangeBody),
          });

          // Mark input as synced so the checkout dirty-check doesn't re-POST
          if (input.defaultValue !== savedQuantity) {
            input.defaultValue = savedQuantity;
          }

          // BPM-5360: skip post-quantity-change /cart/change.js price override PATCHes; properties are set on add-to-cart.

          const cartRes = await fetch('/cart.js');
          const cartData = await cartRes.json();
          const lineItem = this.getCartLineItem(cartData, cartLine);
          const ct = /** @type {{ syncAllAvailabilityBannersForLineItem?: (item: unknown, cartLine: string) => void }} */ (
            document.querySelector('cart-table')
          );
          if (lineItem && ct?.syncAllAvailabilityBannersForLineItem) {
            ct.syncAllAvailabilityBannersForLineItem(lineItem, cartLine);
          }

          if (row instanceof HTMLElement && typeof window.RTP?.refreshCartLineAvailability === 'function') {
            await window.RTP.refreshCartLineAvailability(row);
          }

          // A quantity edit only refreshes this line (no full RTP pass / `cart:rtp-pricing-ready`),
          // so tell the cart summary to re-check the surcharge against the new order total.
          document.dispatchEvent(new CustomEvent('cart:line-quantity-updated'));
        } catch (error) {
          console.error('Error updating line item quantity:', error);
          document.dispatchEvent(new CustomEvent('cart:line-quantity-updated'));
        }
      })();

      this._pendingCartUpdate = updatePromise;
      updatePromise.finally(() => {
        if (this._pendingCartUpdate === updatePromise) {
          this._pendingCartUpdate = null;
        }
      });
    });
  }

  /**
   * @param {any} cart
   * @returns {boolean}
   */
  getShipCompleteFromCartAttributes(cart) {
    const raw =
      cart?.attributes?._ship_complete ?? cart?.attributes?.ship_complete;
    if (raw === undefined || raw === null || String(raw).trim() === '') {
      return false;
    }
    return String(raw).toLowerCase() === 'true';
  }

  /**
   * A row's 1-based cart line number, taken from `data-index`. `data-line-item-id` is the variant
   * id and repeats across lines sharing a variant, so it cannot identify a row. Multi-delivery
   * clone rows carry `data-index="{cartLine}_delivery_{date}"` and resolve to their parent line.
   * @param {Element | null} row
   * @returns {string}
   */
  getCartLineForRow(row) {
    const rowId = row?.getAttribute('data-index') || '';
    return rowId.match(/^(\d+)_delivery_/)?.[1] ?? rowId;
  }

  /**
   * `/cart.js` line for a 1-based cart line number.
   * @param {any} cart
   * @param {string | number} cartLine
   * @returns {any}
   */
  getCartLineItem(cart, cartLine) {
    const index = Number(cartLine);
    if (!Number.isFinite(index) || index < 1) return undefined;
    return cart?.items?.[index - 1];
  }

  /**
   * Resolve the cart line a row belongs to.
   * @param {any} cart
   * @param {Element | null} row
   * @returns {any}
   */
  resolveCartLineForRow(cart, row) {
    return this.getCartLineItem(cart, this.getCartLineForRow(row));
  }

  /**
   * `.rs-table-body__row` for a cart line number, excluding its multi-delivery clones.
   * @param {string | number} cartLine
   * @returns {HTMLElement | null}
   */
  getCartLineRow(cartLine) {
    return /** @type {HTMLElement | null} */ (
      document.querySelector(`.rs-table-body__row[data-index="${cartLine}"]:not([data-multiple-delivery])`)
    );
  }

  /**
   * Multi-delivery clone rows for a cart line, in DOM order.
   * @param {string | number} cartLine
   * @returns {HTMLElement[]}
   */
  getCartLineCloneRows(cartLine) {
    return /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll('.rs-table-body__row[data-multiple-delivery="true"]')).filter(
        (row) => (row.getAttribute('data-index') || '').startsWith(`${cartLine}_delivery_`)
      )
    );
  }

  /**
   * Flag the checkout links while a cart write is in flight. `validateCartQuantities`
   * already owns the href / aria-disabled / `.disabled` handling, so it applies the state.
   * @param {boolean} isPending
   */
  setCheckoutMutationPending(isPending) {
    // Covers the order summary link and the mobile header link, so both get the same
    // disabled state and the same loading dots.
    const checkoutSelector = 'a.cart-your-order-summary__checkout, .cart-page-summary a';

    document.querySelectorAll(checkoutSelector).forEach((checkoutLink) => {
      if (!(checkoutLink instanceof HTMLElement)) return;
      if (isPending) {
        checkoutLink.dataset.cartMutationPending = 'true';
      } else {
        delete checkoutLink.dataset.cartMutationPending;
      }
    });

    // Reuse RTP's checkout loading dots so this matches the initial page load state. The
    // `cart-write` owner keeps them up while RTP resolves pricing in parallel, and RTP's own
    // owner entry keeps them up after this write settles.
    const setCartCheckoutLoading = /** @type {any} */ (window).setCartCheckoutLoading;
    if (typeof setCartCheckoutLoading === 'function') {
      setCartCheckoutLoading(isPending, checkoutSelector, 'cart-write');
    }

    this.validateCartQuantities?.();
  }

  /**
   * Keep checkout disabled until `mutation` settles. Concurrent writes are counted so the
   * first one to finish does not re-enable checkout while others are still in flight.
   * @param {Promise<any>} mutation
   * @returns {Promise<any>}
   */
  async trackCartMutation(mutation) {
    this._pendingCartMutationCount = (this._pendingCartMutationCount || 0) + 1;
    this.setCheckoutMutationPending(true);

    try {
      return await mutation;
    } finally {
      this._pendingCartMutationCount = Math.max(0, this._pendingCartMutationCount - 1);
      if (this._pendingCartMutationCount === 0) {
        this.setCheckoutMutationPending(false);
      }
    }
  }

  /**
   * Run cart writes one at a time. Attribute updates are read-modify-write, so overlapping
   * writers (ship complete, payment terms, sales area) could otherwise POST a snapshot taken
   * before another write landed and drop it. Serializing also keeps the theme from hitting
   * Shopify's back-to-back cart write throttling.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  queueCartWrite(task) {
    /** @type {Promise<any>} */
    const previous = this._cartWriteQueue || Promise.resolve();
    const run = previous.catch(() => {}).then(() => task());
    this._cartWriteQueue = run.catch(() => {});
    return run;
  }

  /**
   * Write only the attributes we own — `/cart/update.js` merges attributes, so re-sending a
   * full snapshot would re-assert values another writer just changed. The result is verified
   * against the returned cart and retried once, since a throttled response leaves the
   * attribute unchanged while still resolving.
   * @param {Record<string, string>} attributes
   * @param {number} [attempt]
   * @returns {Promise<any|null>} Updated cart, or null when the write could not be confirmed
   */
  async writeCartAttributes(attributes, attempt = 0) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({attributes}),
      });
      const cart = await response.json().catch(() => null);
      const persisted =
        response.ok &&
        Object.entries(attributes).every(
          ([key, value]) => String(cart?.attributes?.[key] ?? '') === String(value)
        );

      if (persisted) return cart;

      if (attempt < 1) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        return this.writeCartAttributes(attributes, attempt + 1);
      }

      console.error('Cart attributes did not persist:', attributes, cart);
      return null;
    } catch (error) {
      console.error('Error updating cart attributes:', error);
      return null;
    }
  }

  /**
   * Persist ship-complete toggle to Shopify cart (called after UI toggle).
   * @param {HTMLElement} button
   * @param {boolean} isActive
   */
  persistShipCompleteToggle(button, isActive) {
    const isCartActions = !!button.closest('.cart-page-actions__ship-complete');
    // Header toggle fans out to one `/cart/change.js` per line, so gate checkout for the
    // whole batch — including time spent queued behind another write — rather than letting
    // the user leave mid-way through a partial update.
    return this.trackCartMutation(
      this.queueCartWrite(() => this.updateShipCompleteAttribute(button, isActive, isCartActions))
    );
  }

  /**
   * Get current cart state and sync ship complete buttons with existing attributes
   */
  async syncWithCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Empty carts still retain attributes after line removal; clear them so
      // checkout/order attributes from a prior session do not linger.
      if (!cart?.item_count) {
        if (typeof /** @type {any} */ (window).clearCartAttributes === 'function') {
          await /** @type {any} */ (window).clearCartAttributes(cart);
        }
        return;
      }

      if (/** @type {any} */ (window).rsShipComplete?.syncFromCart) {
        await /** @type {any} */ (window).rsShipComplete.syncFromCart(cart);
      } else {
        const isShipComplete = this.getShipCompleteFromCartAttributes(cart);
        this.syncShipCompleteButtons(isShipComplete);
      }

      if (this.getShipCompleteFromCartAttributes(cart)) {
        this.scheduleShipCompleteBannerDateUpdate();
      }

      /** @type {Record<string, string>} */
      const attributeUpdates = {};

      // Payment terms
      const paymentTerms = document.querySelector('.company-payment-terms');
      if (paymentTerms instanceof HTMLInputElement && paymentTerms.value) {
        attributeUpdates.payment_terms = paymentTerms.value;
      }

      // Sales area
      const salesArea = document?.querySelector('body')?.getAttribute('data-current-sales-area');
      if (salesArea) {
        attributeUpdates._sales_area = salesArea;
      }

      if (Object.keys(attributeUpdates).length > 0) {
        await this.trackCartMutation(
          this.queueCartWrite(() => this.writeCartAttributes(attributeUpdates))
        );
      }
    } catch (error) {
      console.error('Error fetching cart state:', error);
    }
  }

  async syncWithCartLineItems() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    const lineItems = cart.items;
    lineItems.forEach((/** @type {{ properties: { _ship_complete: string; _delivery_date: string; plant: string; availability_schedule: string; }; id: string; }} */ lineItem, /** @type {number} */ lineItemIndex) => {
      const cartLine = lineItemIndex + 1;

      // Plant
      if (lineItem.properties && lineItem.properties._plant) {
        const plant = lineItem.properties._plant;
        this.syncPlant(plant, cartLine);
      }

      // Availability
      if (lineItem.properties && lineItem.properties.availability_schedule) {
        const availability = lineItem.properties.availability_schedule;
        this.syncAvailabilitySchedule(availability, cartLine);
      }

      // Ship Complete
      if (lineItem.properties && lineItem.properties._ship_complete && cart.attributes._ship_complete !== 'true') {
        const isShipComplete = lineItem.properties._ship_complete === 'true';
        this.syncShipCompleteButtons(isShipComplete, cartLine);
      }

      // Requested delivery (primary row + multi-delivery split rows)
      this.syncLineItemRequestedDeliveryInputs(lineItem, cartLine, true);

      // Packing list notes
      if (lineItem.properties && lineItem.properties._packing_list_notes) {
        const packingListNote = lineItem.properties._packing_list_notes;
        this.syncPackingListNote(packingListNote, cartLine);
      }
    });
    this.scheduleShipCompleteBannerDateUpdate();
  }

  /**
   * @param {string} availabilitySchedule
   * @param {string | number} cartLine - 1-based cart line number
   */
  syncAvailabilitySchedule(availabilitySchedule, cartLine) {
    const availabilityScheduleElement = this.getCartLineRow(cartLine)?.querySelector(
      '.cart-table__availability-schedule'
    );
    if (availabilityScheduleElement instanceof HTMLElement) {
      this.createAvailabilityBanner(availabilityScheduleElement, availabilitySchedule);
    }
  }

  /**
   * @param {string} plant
   * @param {string | number} cartLine - 1-based cart line number
   */
  syncPlant(plant, cartLine) {
    const plantElement = this.getCartLineRow(cartLine)?.querySelector('.cart-table__availability-location');
    if (plantElement instanceof HTMLElement) {
      plantElement.innerHTML = plant;
    }
  }

  /**
   * Merge partial line item properties via `/cart/change.js` while preserving quantity and other properties.
   * @param {string | number} cartLine - 1-based cart line number
   * @param {Record<string, unknown>} propertiesPatch
   */
  async updateOriginalLineItem(cartLine, propertiesPatch, quantityOverride) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const currentLineItem = this.getCartLineItem(cart, cartLine);
      if (!currentLineItem) {
        console.warn('updateOriginalLineItem: cart line not found', cartLine);
        return;
      }
      const existingProperties = currentLineItem.properties || {};
      const quantity =
        quantityOverride != null && Number.isFinite(Number(quantityOverride)) && Number(quantityOverride) >= 1
          ? Number(quantityOverride)
          : currentLineItem.quantity ?? 1;
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: Number(cartLine),
          quantity,
          properties: {
            ...existingProperties,
            ...propertiesPatch,
          },
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        console.error('Error updating line item properties:', result);
        this.showErrorNotification('Failed to update line item');
      }
    } catch (error) {
      console.error('Error in updateOriginalLineItem:', error);
    }
  }

  /**
   * Native `type="date"` value uses yyyy-mm-dd; cart properties often use m/d/Y.
   * @param {string} str
   * @returns {string}
   */
  normalizeDeliveryDateToIsoNativeInput(str) {
    const t = String(str || '').trim();
    if (!t) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    const mdY = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdY) return `${mdY[3]}-${mdY[1].padStart(2, '0')}-${mdY[2].padStart(2, '0')}`;
    return t;
  }

  /**
   * Set visible `input.rs-input-type__date` fields in the cell (Flatpickr altInput), excluding the bound `.flatpickr-input`.
   * @param {HTMLElement} wrap
   * @param {string} displayValue
   */
  setRequestedDeliveryVisibleDateInputs(wrap, displayValue) {
    wrap.querySelectorAll('input.rs-input-type__date:not(.flatpickr-input)').forEach((el) => {
      if (el instanceof HTMLInputElement) el.value = displayValue;
    });
  }

  /**
   * Fire `change` for a date the cart already holds, so date validation and any other
   * listeners re-run without `setupPreferredDeliveryDateListeners` reading the cart to
   * confirm a value it just applied.
   * @param {HTMLInputElement} input
   */
  dispatchProgrammaticDeliveryChange(input) {
    this.isApplyingDeliveryDateFromCart = true;
    try {
      input.dispatchEvent(new Event('change', {bubbles: true}));
    } finally {
      this.isApplyingDeliveryDateFromCart = false;
    }
  }

  /**
   * Apply cart `_delivery_date` / split `_requested_deliveries` dates to the row’s requested-delivery UI.
   * Uses `input.flatpickr-input` for `_flatpickr` (visible `.rs-input-type__date` is the alt input and must be updated explicitly after `setDate` when needed).
   * @param {Element} row
   * @param {string} dateStr
   * @param {boolean} dispatchChange
   */
  applyDeliveryDateToRow(row, dateStr, dispatchChange = false) {
    if (!row || !dateStr) return;
    const wrap =
      row.querySelector('.cart-table__requested-delivery-date') || row.querySelector('.rs-table-body__cell--req-delivery');
    if (!(wrap instanceof HTMLElement)) return;

    const trimmed = String(dateStr).trim();

    const fpBound = wrap.querySelector('input.flatpickr-input');
    const fp = fpBound ? /** @type {any} */ (fpBound)._flatpickr : undefined;

    if (fp && typeof fp.setDate === 'function') {
      try {
        fp.setDate(trimmed, false);
      } catch {
        /* fall through */
      }
      if (fp.selectedDates?.[0]) {
        const altFmt =
          typeof fp.config.altFormat === 'string'
            ? fp.config.altFormat
            : typeof fp.config.dateFormat === 'string'
              ? fp.config.dateFormat
              : 'm/d/Y';
        const formatted = fp.formatDate(fp.selectedDates[0], altFmt);
        if (fp.altInput instanceof HTMLInputElement) {
          fp.altInput.value = formatted;
        }
        this.setRequestedDeliveryVisibleDateInputs(wrap, formatted);
      } else {
        this.setRequestedDeliveryVisibleDateInputs(wrap, trimmed);
      }
      const trigger =
        fp.altInput instanceof HTMLInputElement
          ? fp.altInput
          : fpBound instanceof HTMLInputElement
            ? fpBound
            : null;
      if (dispatchChange && trigger) {
        this.dispatchProgrammaticDeliveryChange(trigger);
      }
      return;
    }

    const nativeDate = wrap.querySelector('input[type="date"].rs-input-type__date:not(.flatpickr-input)');
    if (nativeDate instanceof HTMLInputElement) {
      const iso = this.normalizeDeliveryDateToIsoNativeInput(trimmed);
      nativeDate.value = iso || trimmed;
      if (dispatchChange) this.dispatchProgrammaticDeliveryChange(nativeDate);
      return;
    }

    const visible =
      wrap.querySelector('input[type="text"].rs-input-type__date:not(.flatpickr-input)') ||
      wrap.querySelector('input.rs-input-type__date:not(.flatpickr-input)');
    if (visible instanceof HTMLInputElement) {
      visible.value = trimmed;
      this.setRequestedDeliveryVisibleDateInputs(wrap, trimmed);
      if (dispatchChange) this.dispatchProgrammaticDeliveryChange(visible);
    }
  }

  /**
   * Sync primary `_delivery_date` and every multi-delivery clone row from /cart.js (same flatten as cart-table).
   * Clone rows are never skipped: use `data-delivery-date` when set, otherwise the matching entry in flattened `_requested_deliveries`.
   * @param {any} lineItem
   * @param {string | number} cartLine - 1-based cart line number
   * @param {boolean} dispatchPrimaryChange - When false, only updates controls (used after split rows mount to avoid duplicate POSTs on the primary row).
   */
  syncLineItemRequestedDeliveryInputs(lineItem, cartLine, dispatchPrimaryChange = true) {
    if (!lineItem?.id || !cartLine) return;
    const props = lineItem.properties || {};

    const splitDeliveries = this.extraRequestedDeliveryLeaves(props);

    const primaryRow = this.getCartLineRow(cartLine);
    const primaryDate = props._delivery_date;
    if (primaryRow && primaryDate != null && String(primaryDate).trim() !== '') {
      this.applyDeliveryDateToRow(primaryRow, String(primaryDate), dispatchPrimaryChange);
    }

    const cloneRows = this.getCartLineCloneRows(cartLine);

    cloneRows.forEach((row, idx) => {
      const fromAttr = row.getAttribute('data-delivery-date');
      const fromCart = splitDeliveries[idx]?._delivery_date;
      const dateStr = (fromAttr && String(fromAttr).trim()) || (fromCart && String(fromCart).trim()) || '';
      if (!dateStr) return;
      this.applyDeliveryDateToRow(row, dateStr, false);
    });

    const ct = /** @type {{ syncAllAvailabilityBannersForLineItem?: (item: unknown, cartLine: string | number) => void }} */ (
      document.querySelector('cart-table')
    );
    if (ct?.syncAllAvailabilityBannersForLineItem) {
      ct.syncAllAvailabilityBannersForLineItem(lineItem, cartLine);
    }
    this.updateShipCompleteBannerDate();
  }

  /**
   * Runs after `<cart-table>` injects multi-delivery rows so Flatpickr + split row dates can sync without firing primary `change` twice.
   */
  async syncDeliveryDatesAfterDomUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      /** @type {{ items?: unknown[] }} */
      const c = cart;
      (c.items || []).forEach((/** @type {any} */ item, /** @type {number} */ index) => {
        this.syncLineItemRequestedDeliveryInputs(item, index + 1, false);
      });
      this.updateShipCompleteBannerDate();
    } catch (error) {
      console.error('Error syncing delivery dates after row update:', error);
    }
  }

  /**
   * @param {string} packingListNote
   * @param {string | number} cartLine - 1-based cart line number
   */
  syncPackingListNote(packingListNote, cartLine) {
    const rowElement = this.getCartLineRow(cartLine);
    if (rowElement) {
      rowElement.classList.add('cart-items__item-details--notes--active');
      window.PackingListNotes.createPackingListNotesInput(rowElement, packingListNote);
    }
  }

  /**
   * Sync all ship complete buttons with the given state
   * @param {boolean} isActive - Whether ship complete should be active
   * @param {string | number} [cartLine] - 1-based cart line number; omit to sync the cart-level button
   */
  syncShipCompleteButtons(isActive, cartLine) {
    if (cartLine != null && String(cartLine) !== '') {
      const button = this.getCartLineRow(cartLine)?.querySelector(
        '.cart-table__ship-complete .ship-complete-button'
      );
      if (button instanceof HTMLElement) {
        if (/** @type {any} */ (window).RsShipComplete?.setActive) {
          /** @type {any} */ (window).RsShipComplete.setActive(button, isActive);
        } else {
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', String(isActive));
        }
      }
      return;
    }

    const cartLevelButton = document.querySelector(
      '.cart-page-actions__ship-complete .ship-complete-button'
    );
    if (cartLevelButton instanceof HTMLElement) {
      if (/** @type {any} */ (window).RsShipComplete?.setActive) {
        /** @type {any} */ (window).RsShipComplete.setActive(cartLevelButton, isActive);
      } else {
        cartLevelButton.classList.toggle('is-active', isActive);
        cartLevelButton.setAttribute('aria-pressed', String(isActive));
      }
      /** @type {any} */ (window).RsShipComplete?.showShipCompleteBanner?.(isActive);
    }

    document.querySelectorAll('.cart-table__ship-complete .ship-complete-button').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      if (/** @type {any} */ (window).RsShipComplete?.setActive) {
        /** @type {any} */ (window).RsShipComplete.setActive(button, isActive);
      } else {
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      }
    });

    if (isActive) {
      this.scheduleShipCompleteBannerDateUpdate();
    }
  }

  /**
   * @param {HTMLElement} availabilityScheduleElement
   * @param {string} availabilitySchedule
   */
  createAvailabilityBanner(availabilityScheduleElement, availabilitySchedule) {
    const bannerHTML = `<span class="bg-gray-100 cart-table__availability-banner">${availabilitySchedule.quantity} units will ship on<strong class="gray-600"> ${availabilitySchedule.date}</strong></span>`;
    availabilityScheduleElement.insertAdjacentHTML('afterbegin', bannerHTML);
    this.updateShipCompleteBannerDate();
  }

  /**
   * Ensure banner date refresh runs after line-item availability DOM updates.
   */
  scheduleShipCompleteBannerDateUpdate() {
    requestAnimationFrame(() => {
      this.updateShipCompleteBannerDate();
    });
  }

  /**
   * Wrap RsShipComplete banner show so the date always reflects current line availability.
   */
  wrapShipCompleteBannerShow() {
    const patchShow = (/** @type {any} */ target) => {
      if (!target?.showShipCompleteBanner) return;
      const original = target.showShipCompleteBanner.bind(target);
      target.showShipCompleteBanner = (/** @type {boolean} */ active) => {
        original(active);
        if (active) {
          this.scheduleShipCompleteBannerDateUpdate();
        }
      };
    };

    patchShow(/** @type {any} */ (window).RsShipComplete);
    patchShow(/** @type {any} */ (window).rsShipComplete);
  }

  /**
   * @param {string} str
   * @returns {Date | null}
   */
  parseAvailabilityShipDate(str) {
    const t = String(str || '').trim();
    if (!t) return null;

    const mdY = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mdY) {
      return new Date(Number(mdY[3]), Number(mdY[1]) - 1, Number(mdY[2]));
    }

    const parsed = Date.parse(t);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed);
    }

    return null;
  }

  /**
   * @param {string} text
   * @returns {Date | null}
   */
  parseShipDateFromBannerText(text) {
    const t = String(text || '').trim();
    if (!t) return null;

    const mdY = t.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
    if (mdY?.[1]) {
      const d = this.parseAvailabilityShipDate(mdY[1]);
      if (d) return d;
    }

    return this.parseAvailabilityShipDate(t);
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  formatShipDateMDY(date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  /**
   * Furthest (latest) available ship date on one cart row, read from its availability copy
   * (banners, ATP list, or the combined ship-complete banner). The row's requested-delivery
   * input is deliberately ignored so a date already chosen can't stand in for availability.
   * @param {Element} row
   * @returns {Date | null}
   */
  findLatestShipDateForRow(row) {
    /** @type {Date | null} */
    let furthest = null;

    const combined = row.querySelector('.cart-table__availability-banner--combined');
    const banners = combined
      ? [combined]
      : [
          ...row.querySelectorAll('.cart-table__availability-banner, .rs-cart-line-availability .rs-table__item'),
        ].filter((banner) => {
          if (!(banner instanceof HTMLElement)) return false;
          return banner.style.display !== 'none' && !banner.hidden && !banner.classList.contains('rs-hidden');
        });

    banners.forEach((banner) => {
      const strong = banner.querySelector('strong.gray-600');
      const sources = [strong?.textContent?.trim(), banner.textContent?.trim()].filter(Boolean);
      const seen = new Set();

      sources.forEach((raw) => {
        const mdY = /(\d{1,2}\/\d{1,2}\/\d{4})/g;
        let match;
        while ((match = mdY.exec(/** @type {string} */ (raw))) !== null) {
          const d = this.parseAvailabilityShipDate(match[1]);
          if (!d) continue;
          const key = d.getTime();
          if (seen.has(key)) continue;
          seen.add(key);
          if (!furthest || d > furthest) furthest = d;
        }
        if (!seen.size) {
          const d = this.parseAvailabilityShipDate(/** @type {string} */ (raw));
          if (d) {
            const key = d.getTime();
            if (!seen.has(key)) {
              seen.add(key);
              if (!furthest || d > furthest) furthest = d;
            }
          }
        }
      });
    });

    return furthest;
  }

  /**
   * Furthest (latest) available ship date across all cart line rows.
   * @returns {string | null}
   */
  findLatestLineItemShipDate() {
    const findLatest = /** @type {any} */ (window).rsShipComplete?.findLatestShipDate;
    if (typeof findLatest === 'function') {
      const furthest = findLatest();
      if (furthest instanceof Date) {
        return this.formatShipDateMDY(furthest);
      }
    }

    /** @type {Date | null} */
    let furthest = null;

    document.querySelectorAll('.cart-table .rs-table-body__row').forEach((row) => {
      const rowLatest = this.findLatestShipDateForRow(row);
      if (rowLatest && (!furthest || rowLatest > furthest)) furthest = rowLatest;
    });

    return furthest ? this.formatShipDateMDY(furthest) : null;
  }

  /**
   * Update ship-complete banner copy when visible (date line hidden if no availability date).
   */
  updateShipCompleteBannerDate() {
    const banner = document.querySelector('.cart-page-actions__ship-complete-banner');
    if (!(banner instanceof HTMLElement) || !banner.classList.contains('is-active')) {
      return;
    }

    const updater = /** @type {any} */ (window).rsShipComplete?.updateShipCompleteBanner;
    if (typeof updater === 'function') {
      updater();
      return;
    }

    const bannerDate = banner.querySelector('.ship-complete-banner__date');
    const dateDetail = banner.querySelector('.ship-complete-banner__date-detail');
    const latest =
      this.findLatestLineItemShipDate() ||
      (/** @type {any} */ (window).rsShipComplete?.findLatestShipDate &&
        (() => {
          const d = /** @type {any} */ (window).rsShipComplete.findLatestShipDate();
          return d instanceof Date ? this.formatShipDateMDY(d) : null;
        })());

    if (latest && bannerDate) {
      bannerDate.textContent = latest;
      if (dateDetail instanceof HTMLElement) {
        dateDetail.hidden = false;
      }
    } else if (dateDetail instanceof HTMLElement) {
      dateDetail.hidden = true;
      if (bannerDate) {
        bannerDate.textContent = '';
      }
    }
  }

  /**
   * Setup event listeners for ship complete toggles
   */
  setupShipCompleteListeners() {
    // Persistence runs from rs-ship-complete toggleShipComplete → persistShipCompleteToggle
  }

  /**
   * Keep cart-level `_ship_complete` / `ship_complete` aligned with line-item toggles.
   */
  async syncCartShipCompleteAttributeFromLineItems() {
    try {
      const cart = await this.getCurrentCartState();
      if (!cart?.items?.length) return;

      const lineButtons = document.querySelectorAll('.cart-table__ship-complete .ship-complete-button');
      const total = lineButtons.length;
      let activeCount = 0;
      lineButtons.forEach((btn) => {
        if (btn.classList.contains('is-active')) activeCount += 1;
      });

      const allActive = total > 0 && activeCount === total;
      const shipCompleteValue = allActive ? 'true' : 'false';
      const existingAttributes = cart.attributes || {};

      if (
        String(existingAttributes._ship_complete).toLowerCase() === shipCompleteValue &&
        String(existingAttributes.ship_complete).toLowerCase() === shipCompleteValue
      ) {
        return;
      }

      await this.writeCartAttributes({
        _ship_complete: shipCompleteValue,
        ship_complete: shipCompleteValue,
      });
    } catch (error) {
      console.error('Error syncing cart ship complete attribute from line items:', error);
    }
  }

  /**
   * Persist one line's `_ship_complete` from an already-fetched cart line, preserving its
   * other properties. Shopify has no bulk line-property endpoint, so callers that need to
   * update many lines pass each resolved line here instead of re-fetching `/cart.js`.
   * When ship complete is on, `_ship_complete_date` is the latest available-to-ship date
   * for that row; turning it off drops the property.
   * @param {any} lineItem - Line object from `/cart.js` (may be undefined)
   * @param {boolean} shipComplete
   * @param {string | number} cartLine - 1-based cart line number
   * @returns {Promise<any>}
   */
  async changeLineItemShipComplete(lineItem, shipComplete, cartLine) {
    const line = Number(cartLine);
    if (!Number.isFinite(line) || line < 1) return null;

    const properties = {
      ...(lineItem?.properties || {}),
      _ship_complete: shipComplete.toString(),
    };

    if (shipComplete) {
      const row = this.getCartLineRow(cartLine);
      const latest = row ? this.findLatestShipDateForRow(row) : null;
      if (latest) {
        properties._ship_complete_date = this.formatShipDateMDY(latest);
      }
    } else {
      delete properties._ship_complete_date;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        line,
        // Preserve existing quantity; Shopify may default to 1 if omitted
        quantity: lineItem?.quantity ?? 1,
        properties,
      }),
    });

    const result = await response.json();
    if (!response.ok) {
      console.error('Error updating line item ship complete:', result);
      throw new Error('Line item ship complete update failed');
    }
    return result;
  }

  /**
   * Update cart attributes using Shopify AJAX API
   * @param {HTMLElement} button - The ship complete button element
   * @param {boolean} shipComplete - Whether ship complete is active
   * @param {boolean} isCartActionsShipComplete
   */
  async updateShipCompleteAttribute(button, shipComplete, isCartActionsShipComplete) {
    // If the ship complete is being updated from the cart actions header, update the cart attributes
    if (isCartActionsShipComplete) {
      try {
        // Single cart snapshot drives every line change, instead of re-fetching `/cart.js`
        // and re-syncing attributes once per line.
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const updatedCart = await this.writeCartAttributes({
          _ship_complete: shipComplete.toString(),
          ship_complete: shipComplete.toString(),
        });

        if (!updatedCart) {
          this.showErrorNotification('Failed to update ship complete status');
          // The toggle already flipped optimistically; re-read the cart so the UI can't
          // claim ship complete is on while the attribute says otherwise.
          await /** @type {any} */ (window).rsShipComplete?.syncFromCart?.();
          return;
        }

        // Shopify has no bulk line-property update, so each line still needs its own
        // `/cart/change.js`; run them sequentially from the snapshot above to avoid 409s.
        for (const [index, line] of (cart.items || []).entries()) {
          try {
            await this.changeLineItemShipComplete(line, shipComplete, index + 1);
          } catch (error) {
            console.error('Error updating ship complete for line:', error);
            // Continue with next line even if one fails
          }
        }

        this.showSuccessNotification('Ship complete status updated');
      } catch (error) {
        console.error('Error updating cart attributes:', error);
        this.showErrorNotification('Failed to update ship complete status');
      }
    } else {
      // If the ship complete is being updated from the line item, update the line item
      const lineItem = button.closest('.rs-table-body__row');
      const cartLine = this.getCartLineForRow(lineItem);

      if (cartLine) {
        try {
          // First, get the current cart to preserve existing line item properties
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          const currentLineItem = this.getCartLineItem(cart, cartLine);
          const result = await this.changeLineItemShipComplete(currentLineItem, shipComplete, cartLine);

          await this.syncCartShipCompleteAttributeFromLineItems();
          this.showSuccessNotification('Line item ship complete status updated');
          return result;
        } catch (error) {
          console.error('Error updating line item ship complete:', error);
          this.showErrorNotification('Failed to update line item ship complete status');
        }
      } else {
        console.warn('Cart line not found for ship complete update');
      }
    }
  }

  /**
   * Canonical key so `04/01/2026`, `4/1/2026`, and `2026-04-01` compare equal when patching cart properties.
   * @param {string} str
   * @returns {string}
   */
  normalizeDeliveryDateMatchKey(str) {
    const t = String(str || '').trim();
    if (!t) return '';
    const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const us = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (us && us[1] && us[2] && us[3]) {
      return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
    }
    return t.toLowerCase();
  }

  /**
   * `_requested_deliveries` may be an array or a JSON string from Shopify / older saves.
   * @param {unknown} raw
   * @returns {unknown[]}
   */
  parseLineItemRequestedDeliveries(raw) {
    if (raw == null) return [];
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(raw) ? raw : [];
  }

  /**
   * Quantity + date from a `_requested_deliveries` leaf. Accepts `{quantity,_delivery_date}` and compact `{q,d}`.
   * @param {unknown} d
   * @returns {{ quantity: number; _delivery_date: string } | null}
   */
  readRequestedDeliveryLeaf(d) {
    if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
    const rec = /** @type {Record<string, unknown>} */ (d);
    const date = String(rec._delivery_date ?? rec.d ?? '').trim();
    const quantity = Number(rec.quantity ?? rec.q);
    if (!date || !Number.isFinite(quantity) || Number.isNaN(quantity)) return null;
    return {quantity, _delivery_date: date};
  }

  /**
   * @param {unknown} node
   * @returns {string}
   */
  getRequestedDeliveryDateFromLeaf(node) {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return '';
    const rec = /** @type {Record<string, unknown>} */ (node);
    return String(rec._delivery_date ?? rec.d ?? '').trim();
  }

  /**
   * Uses compact `{q,d}` keys when verbose JSON would exceed Shopify's 255-character property cap.
   * @param {unknown[]} leaves
   * @returns {string}
   */
  compactRequestedDeliveriesForShopify(leaves) {
    const normalized = (Array.isArray(leaves) ? leaves : [])
      .map((l) => {
        if (!l || typeof l !== 'object' || Array.isArray(l)) return null;
        const rec = /** @type {Record<string, unknown>} */ ({...l});
        const quantity = Number(rec.quantity ?? rec.q);
        const date = String(rec._delivery_date ?? rec.d ?? '').trim();
        delete rec.quantity;
        delete rec._delivery_date;
        delete rec.q;
        delete rec.d;
        if (!date || !Number.isFinite(quantity) || quantity <= 0) return null;
        return {quantity, _delivery_date: date, rest: rec};
      })
      .filter((l) => l != null);

    const full = normalized.map((l) => ({quantity: l.quantity, _delivery_date: l._delivery_date, ...l.rest}));
    const payload =
      JSON.stringify(full).length <= 255
        ? full
        : normalized.map((l) => ({q: l.quantity, d: l._delivery_date, ...l.rest}));
    return JSON.stringify(payload);
  }

  /**
   * Leaf splits for `_requested_deliveries` (nested batches, flat arrays, JSON strings) — keep in sync with `<cart-table>`.
   * @param {unknown} raw
   * @returns {unknown[]}
   */
  flattenRequestedDeliveriesLeaves(raw) {
    const parsed = this.parseLineItemRequestedDeliveries(raw);
    return parsed
      .flat(Infinity)
      .map((d) => this.readRequestedDeliveryLeaf(d))
      .filter((d) => d != null);
  }

  /**
   * `_requested_deliveries` holds every split, primary first, so its leading entry mirrors
   * `_delivery_date`. Carts saved before that change hold splits 2+ only.
   * @param {Record<string, unknown>} properties
   * @param {unknown[]} leaves
   * @returns {boolean}
   */
  requestedDeliveriesIncludePrimary(properties, leaves) {
    if (!leaves.length) return false;
    const primaryDate = this.normalizeDeliveryDateMatchKey(String(properties?._delivery_date ?? ''));
    if (!primaryDate) return false;
    const firstDate = this.getRequestedDeliveryDateFromLeaf(leaves[0]);
    return this.normalizeDeliveryDateMatchKey(firstDate) === primaryDate;
  }

  /**
   * Splits rendered as clone rows — every requested delivery except the primary one.
   * @param {Record<string, unknown>} properties
   * @returns {unknown[]}
   */
  extraRequestedDeliveryLeaves(properties) {
    const leaves = this.flattenRequestedDeliveriesLeaves(properties?._requested_deliveries);
    return this.requestedDeliveriesIncludePrimary(properties, leaves) ? leaves.slice(1) : leaves;
  }

  /**
   * Quantity currently shown on a cart row, or 0 when the row has no quantity input.
   * @param {Element | null | undefined} row
   * @returns {number}
   */
  getRowQuantityValue(row) {
    const input = row?.querySelector('.rs-table-body__cell--quantity input.rs-quantity-input');
    return input instanceof HTMLInputElement ? parseInt(input.value, 10) || 0 : 0;
  }

  /**
   * `/cart/change.js` body for a quantity edit. When the line has split delivery rows, rebuild
   * `_requested_deliveries` from the rows so it carries every split's date and quantity (primary
   * first), and send the sum of those splits as the Shopify line quantity.
   * @param {string | number} cartLine - 1-based cart line number
   * @param {string | number} editedQuantity - value now shown on the edited row
   * @returns {Promise<{ line: number; quantity: number | string; properties?: Record<string, unknown> }>}
   */
  async buildQuantityChangeBody(cartLine, editedQuantity) {
    const cloneRows = this.getCartLineCloneRows(cartLine);
    if (cloneRows.length === 0) {
      return {line: Number(cartLine), quantity: editedQuantity};
    }

    const primaryQty = this.getRowQuantityValue(this.getCartLineRow(cartLine));

    const cartRes = await fetch('/cart.js');
    const cartData = await cartRes.json();
    const lineItem = this.getCartLineItem(cartData, cartLine);
    if (!lineItem) throw new Error('Line item not found for cart line: ' + cartLine);

    const props = /** @type {Record<string, unknown>} */ ({...(lineItem.properties || {})});
    const leaves = /** @type {any[]} */ (this.flattenRequestedDeliveriesLeaves(props._requested_deliveries));
    if (leaves.length === 0) {
      // Clone rows exist without stored splits — keep the line total in sync but leave properties alone.
      const cloneQtySum = cloneRows.reduce((sum, clone) => sum + this.getRowQuantityValue(clone), 0);
      return {line: Number(cartLine), quantity: Math.max(1, primaryQty + cloneQtySum)};
    }

    const includesPrimary = this.requestedDeliveriesIncludePrimary(props, leaves);
    const primaryLeaf = includesPrimary ? leaves[0] : null;
    const extras = includesPrimary ? leaves.slice(1) : leaves;

    // Clone rows and split entries are ordered alike (see `syncLineItemRequestedDeliveryInputs`).
    const updated = extras.map((leaf, idx) => {
      const rowQty = this.getRowQuantityValue(cloneRows[idx]);
      return rowQty > 0 ? {...leaf, quantity: rowQty} : leaf;
    });

    const primaryDate = String((primaryLeaf?._delivery_date ?? props._delivery_date) || '').trim();
    if (primaryDate) {
      updated.unshift({
        ...(primaryLeaf || {}),
        _delivery_date: primaryDate,
        quantity: primaryQty > 0 ? primaryQty : Number(primaryLeaf?.quantity) || 0,
      });
    }

    props._requested_deliveries = this.compactRequestedDeliveriesForShopify(updated);

    const total = updated.reduce((sum, leaf) => {
      const q = Number(leaf.quantity);
      return sum + (Number.isFinite(q) && q > 0 ? q : 0);
    }, 0);

    return {
      line: Number(cartLine),
      quantity: Math.max(1, total),
      properties: props,
    };
  }

  /**
   * Requested-delivery cell value for cart properties (primary `_delivery_date` or split-row `_requested_deliveries` patch).
   * Resolves from Flatpickr (alt + hidden) so we persist the real selection even when `change` fires on the alt input.
   * @param {HTMLElement} row
   * @param {HTMLInputElement} eventInput
   * @returns {string}
   */
  getRequestedDeliveryDateInputValue(row, eventInput) {
    const wrap = row.querySelector('.cart-table__requested-delivery-date');
    const hidden =
      wrap?.querySelector('input.flatpickr-input') ||
      wrap?.querySelector('input[type="date"].rs-input-type__date:not(.flatpickr-input)') ||
      wrap?.querySelector('input');
    const fp = hidden ? /** @type {any} */ (hidden)._flatpickr : undefined;
    if (fp?.altInput?.value && String(fp.altInput.value).trim() !== '') {
      return String(fp.altInput.value).trim();
    }
    if (fp?.input?.value && String(fp.input.value).trim() !== '') {
      return String(fp.input.value).trim();
    }
    if (hidden instanceof HTMLInputElement && hidden.value && hidden.value.trim() !== '') {
      return hidden.value.trim();
    }
    return eventInput.value.toString().trim();
  }

  /**
   * Deep-replace one `_delivery_date` leaf in `_requested_deliveries` (used when a split row date changes).
   * Matches dates using normalized keys so cart JSON and DOM attributes can differ in formatting.
   * @param {Record<string, unknown>} existingProperties
   * @param {string} oldDeliveryDate
   * @param {string} newDeliveryDate
   */
  mergeRequestedDeliveriesDateReplace(existingProperties, oldDeliveryDate, newDeliveryDate) {
    const out = {...existingProperties};
    const parsed = this.parseLineItemRequestedDeliveries(out._requested_deliveries);
    if (!parsed.length) return out;

    const targetOld = this.normalizeDeliveryDateMatchKey(oldDeliveryDate);
    if (!targetOld) return out;

    const cloneNested = JSON.parse(JSON.stringify(parsed));

    const walk = (/** @type {unknown[]} */ nodes) => {
      if (!Array.isArray(nodes)) return;
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (Array.isArray(node)) walk(node);
        else if (node && typeof node === 'object' && this.getRequestedDeliveryDateFromLeaf(node)) {
          const o = /** @type {Record<string, unknown>} */ (node);
          const cur = this.normalizeDeliveryDateMatchKey(this.getRequestedDeliveryDateFromLeaf(o));
          if (cur && cur === targetOld) {
            if ('d' in o && !('_delivery_date' in o)) o.d = newDeliveryDate;
            else o._delivery_date = newDeliveryDate;
          }
        }
      }
    };
    walk(/** @type {unknown[]} */ (cloneNested));
    out._requested_deliveries = this.compactRequestedDeliveriesForShopify(
      /** @type {unknown[]} */ (cloneNested).flat(Infinity)
    );
    return out;
  }

  /**
   * Add packing list notes to the requested-delivery entry matching a split row's date.
   * Preserves the existing nested `_requested_deliveries` structure and all other properties.
   * @param {Record<string, unknown>} existingProperties
   * @param {string} deliveryDate
   * @param {string} packingListNotes
   * @returns {Record<string, unknown> | null}
   */
  mergeRequestedDeliveryPackingListNotes(existingProperties, deliveryDate, packingListNotes) {
    const out = {...existingProperties};
    const parsed = this.parseLineItemRequestedDeliveries(out._requested_deliveries);
    const targetDate = this.normalizeDeliveryDateMatchKey(deliveryDate);
    if (!parsed.length || !targetDate) return null;

    const cloneNested = JSON.parse(JSON.stringify(parsed));
    let matched = false;

    const walk = (/** @type {unknown[]} */ nodes) => {
      if (!Array.isArray(nodes)) return;
      nodes.forEach((node) => {
        if (Array.isArray(node)) {
          walk(node);
          return;
        }
        if (!node || typeof node !== 'object' || Array.isArray(node) || !this.getRequestedDeliveryDateFromLeaf(node)) return;

        const delivery = /** @type {Record<string, unknown>} */ (node);
        const currentDate = this.normalizeDeliveryDateMatchKey(this.getRequestedDeliveryDateFromLeaf(delivery));
        if (currentDate === targetDate) {
          delivery._packing_list_notes = packingListNotes;
          matched = true;
        }
      });
    };

    walk(/** @type {unknown[]} */ (cloneNested));
    if (!matched) return null;

    out._requested_deliveries = this.compactRequestedDeliveriesForShopify(
      /** @type {unknown[]} */ (cloneNested).flat(Infinity)
    );
    return out;
  }

  setupPreferredDeliveryDateListeners() {
    const tbody = document.querySelector('.cart-table__body');
    if (!tbody) return;

    tbody.addEventListener('change', (event) => {
      // Replaying a date read from the cart can never be an edit, and reading `/cart.js` to
      // confirm that costs one request per line on load.
      if (this.isApplyingDeliveryDateFromCart) return;

      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.closest('.rs-table-body__cell--req-delivery')) return;

      const row = input.closest('.rs-table-body__row');
      if (!(row instanceof HTMLElement)) return;

      const rowIdAttr = row.getAttribute('data-index') || '';
      const isCloneRow = row.getAttribute('data-multiple-delivery') === 'true';
      const cloneParts = rowIdAttr.match(/^(\d+)_delivery_(.+)$/);
      const cartLine = isCloneRow && cloneParts ? cloneParts[1] : rowIdAttr;

      if (!cartLine) {
        console.warn('Cart line not found for requested delivery update');
        return;
      }

      // `syncLineItemRequestedDeliveryInputs` re-dispatches `change` on every line's date
      // input, so this fires once per cart line at sync time. Queue the writes instead of
      // letting each one read `/cart.js` and POST `/cart/change.js` in parallel, which
      // Shopify rejects with 409.
      this.trackCartMutation(
        this.queueCartWrite(() =>
          this.persistRequestedDeliveryDate(row, input, cartLine, isCloneRow, cloneParts)
        )
      );
    });
  }

  /**
   * Persist a row's requested delivery date to its cart line. Must run inside `queueCartWrite`.
   * @param {HTMLElement} row
   * @param {HTMLInputElement} input
   * @param {string} cartLine - 1-based cart line number
   * @param {boolean} isCloneRow
   * @param {RegExpMatchArray | null} cloneParts
   */
  async persistRequestedDeliveryDate(row, input, cartLine, isCloneRow, cloneParts) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const currentLineItem = this.getCartLineItem(cart, cartLine);
      if (!currentLineItem) {
        console.warn('Cart line item not found for delivery date update');
        return;
      }

      const existingProperties = currentLineItem.properties || {};
      const newDate = this.getRequestedDeliveryDateInputValue(row, input);

      /** @type {Record<string, unknown>} */
      let updatedProperties;

      if (isCloneRow) {
        const oldDate = row.getAttribute('data-delivery-date') || (cloneParts ? cloneParts[2] : '') || '';
        if (!oldDate) {
          console.warn('Could not resolve previous delivery date for split row');
          return;
        }
        // Programmatic syncs replay the cart's own date; nothing to replace in that case.
        if (this.normalizeDeliveryDateMatchKey(oldDate) === this.normalizeDeliveryDateMatchKey(newDate)) return;
        updatedProperties = this.mergeRequestedDeliveriesDateReplace(/** @type {Record<string, unknown>} */ (existingProperties), oldDate, newDate);
      } else {
        const currentDate = String(/** @type {any} */ (existingProperties)._delivery_date ?? '');
        if (this.normalizeDeliveryDateMatchKey(currentDate) === this.normalizeDeliveryDateMatchKey(newDate)) return;
        // The primary split is also stored in `_requested_deliveries`, so move both.
        updatedProperties = this.mergeRequestedDeliveriesDateReplace(
          /** @type {Record<string, unknown>} */ (existingProperties),
          currentDate,
          newDate
        );
        updatedProperties._delivery_date = newDate;
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: Number(cartLine),
          quantity: currentLineItem?.quantity ?? 1,
          properties: updatedProperties,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        if (isCloneRow) {
          row.setAttribute('data-delivery-date', newDate);
          row.setAttribute('data-index', `${cartLine}_delivery_${newDate}`);
        }
        const item = this.getCartLineItem(result, cartLine);
        const ct = /** @type {{ syncAllAvailabilityBannersForLineItem?: (line: unknown, cartLine: string) => void }} */ (
          document.querySelector('cart-table')
        );
        if (item && ct?.syncAllAvailabilityBannersForLineItem) {
          ct.syncAllAvailabilityBannersForLineItem(item, cartLine);
        }
        this.showSuccessNotification('Line item ship complete status updated');
      } else {
        console.error('Error updating line item ship complete:', result);
        this.showErrorNotification('Failed to update line item ship complete status');
      }
    } catch (error) {
      console.error('Error updating line item ship complete:', error);
      this.showErrorNotification('Failed to update line item ship complete status');
    }
  }

  /**
   * Prevent checkout if any outdated date error
   */
  setupDeliveryDateValidation() {
    const checkoutButton = document.querySelector('button[form="cart-form"][name="checkout"]');
    const checkoutForm = document.getElementById('cart-form');
    const checkoutLinks = Array.from(
      document.querySelectorAll('.cart-your-order-summary__checkout, .cart-page-summary a')
    );

    if (!checkoutButton && !checkoutLinks.length) return;

    const validateDates = () => {
      const visibleErrors = Array.from(document.querySelectorAll('.rs-input__date-outdated')).filter((el) => el.offsetParent !== null);

      const hasVisibleError = visibleErrors.length > 0;

      if (checkoutButton) {
        checkoutButton.disabled = hasVisibleError;
        checkoutButton.classList.toggle('disabled', hasVisibleError);
      }

      checkoutLinks.forEach((link) => {
        const blocked = hasVisibleError || link.dataset.rtpCheckoutBlocked === 'true' || link.dataset.rtpCheckoutLoading === 'true' || link.dataset.accountBlockCheckout === 'true' || link.dataset.cartMutationPending === 'true';
        link.classList.toggle('disabled', blocked);
        link.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        if (blocked && (link.dataset.rtpCheckoutBlocked === 'true' || link.dataset.rtpCheckoutLoading === 'true' || link.dataset.accountBlockCheckout === 'true' || link.dataset.cartMutationPending === 'true') && !hasVisibleError) {
          if (!link.dataset.originalHref && link.getAttribute('href')) {
            link.dataset.originalHref = link.getAttribute('href') || '';
          }
          link.removeAttribute('href');
        }
      });

      // If no date errors, let quantity validation re-assert its own disabled state
      if (!hasVisibleError) {
        this.validateCartQuantities?.();
      }
    };

    checkoutLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        if (link.classList.contains('disabled')) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    });

    document.addEventListener('change', validateDates);
    document.addEventListener('input', validateDates);
    // No 'click' listener — clicks don't affect date validity and would override quantity validation state

    // Prevent form submission manually (Enter key form submission)
    if (checkoutForm) {
      checkoutForm.addEventListener('submit', (e) => {
        const hasVisibleError = Array.from(document.querySelectorAll('.rs-input__date-outdated')).some((el) => el.offsetParent !== null);
        if (hasVisibleError) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      });
    }

    // initial check on load
    validateDates();
  }

  /**
   * Update the packing list notes for a line item
   * @param {any} packingListNotes - The packing list notes
   * @param {string | number} cartLine - 1-based cart line number
   * @param {string} deliveryDate - Split delivery date, or empty for the primary row
   */
  async updatePackingListNotes(packingListNotes, cartLine, deliveryDate = '') {
    if (packingListNotes !== ' ') {
      try {
        // First, get the current cart to preserve existing line item properties
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find the current line item to get its existing properties
        const currentLineItem = this.getCartLineItem(cart, cartLine);
        if (!currentLineItem) {
          throw new Error(`Line item not found for cart line: ${cartLine}`);
        }
        const existingProperties = currentLineItem?.properties || {};

        // Primary rows store notes at the line-item level. Split rows store notes on
        // the matching delivery entry so each delivery date can have different notes.
        const updatedProperties = deliveryDate
          ? this.mergeRequestedDeliveryPackingListNotes(existingProperties, deliveryDate, packingListNotes)
          : {
              ...existingProperties,
              _packing_list_notes: packingListNotes,
            };
        if (!updatedProperties) {
          throw new Error(`Requested delivery not found for date: ${deliveryDate}`);
        }

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: Number(cartLine),
            // Preserve existing quantity; Shopify may default to 1 if omitted
            quantity: currentLineItem.quantity,
            properties: updatedProperties,
          }),
        });

        const result = await response.json();

        if (response.ok) {
          this.showSuccessNotification('Line item packing list notes updated');
        } else {
          console.error('Error updating line item packing list notes:', result);
          this.showErrorNotification('Failed to update line item packing list notes');
        }
      } catch (error) {
        console.error('Error updating line item packing list notes:', error);
        this.showErrorNotification('Failed to update line item packing list notes');
      }
    }
  }

  /**
   * Get current cart state from Shopify
   * @returns {Promise<Object|null>} Cart object or null if error
   */
  async getCurrentCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart;
    } catch (error) {
      console.error('Error fetching cart state:', error);
      return null;
    }
  }

  /**
   * Check if ship complete is currently active in the cart
   * @returns {Promise<boolean>} Whether ship complete is active
   */
  async isShipCompleteActive() {
    const cart = await this.getCurrentCartState();
    return cart ? this.getShipCompleteFromCartAttributes(cart) : false;
  }

  /**
   * Show success notification to user
   * @param {string} message - Success message
   */
  showSuccessNotification(message) {
    // You can customize this to match your notification system
    console.log('✅', message);
    // Example: show a toast notification
    if (typeof (/** @type {any} */ (window).showNotification) === 'function') {
      /** @type {any} */ (window).showNotification(message, 'success');
    }
  }

  /**
   * Show error notification to user
   * @param {string} message - Error message
   */
  showErrorNotification(message) {
    // You can customize this to match your notification system
    console.error('❌', message);
    // Example: show a toast notification
    if (typeof (/** @type {any} */ (window).showNotification) === 'function') {
      /** @type {any} */ (window).showNotification(message, 'error');
    }
  }
}

// Make CartMutations available globally
/** @type {any} */
(window).CartMutations = CartMutations;

// Initialize cart mutations when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  /** @type {any} */
  (window).cartMutationsInstance = new CartMutations();
});
